import { ChevronRight, LogOut, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { addToast } from 'components/Toast/Toast'
// import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { clearAllCachesForFan, clearAllCachesForModel } from 'helpers/clearAllCacheStores'
import { logoutUser } from 'services/endpoints/auth'
import {
    useAccessToken,
    useAuthStore,
    useIsLoggingOut,
    useSetIsLoggingOut,
    useUserProfile
} from 'store'
import colors from 'styles/colors'
import { useLocalStorageClear } from 'utils'


interface DropdownContentProps {
    open: boolean
    handleClose: () => void
}

export function DropdownContent({
    open,
    handleClose
}: DropdownContentProps) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const userData = useUserProfile()
    const isLoggingOut = useIsLoggingOut()

    const clearSessionCaches = async (role: 'model' | 'fan') => {
        await queryClient.cancelQueries()
        queryClient.clear()

        if (role === 'model') {
            clearAllCachesForModel()
        } else {
            clearAllCachesForFan()
        }
    }

    const finishLocalLogout = async (role: 'model' | 'fan') => {
        await clearSessionCaches(role)

        useAuthStore.setState({
            accessToken: '',
            userProfile: null
        })

        useLocalStorageClear()

        navigate('/', {
            replace: true
        })

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                useSetIsLoggingOut(false)
            })
        })
    }

    const handleLogout = async () => {
        if (isLoggingOut) return

        const role = userData?.role === 'model' ? 'model' : 'fan'
        const token = useAccessToken()

        useSetIsLoggingOut(true)

        if (!token) {
            handleClose()
            await finishLocalLogout(role)
            return
        }

        const success = await logoutUser(token)

        if (!success) {
            useSetIsLoggingOut(false)

            addToast(
                'error',
                t('error:errorSomethingWentWrong')
            )

            return
        }

        handleClose()
        await finishLocalLogout(role)
    }

    /*
     * Confirmation modal version.
     *
     * Keep this commented out for now while logout
     * should happen immediately.
     *
     * const handleLogoutWithConfirmation = () => {
     *     modalData.addModal(
     *         `${t('logOut')}?`,
     *         <ConfirmModal
     *             body='Are you sure you want to logout?'
     *             confirmFn={handleLogout}
     *         />,
     *         false,
     *         false,
     *         'modal--desktoplogout'
     *     )
     * }
     */

    const goToAccountSettings = () => {
        handleClose()
        navigate('/settings/account')
    }

    const displayName =
        [userData?.first_name, userData?.last_name]
            .filter(Boolean)
            .join(' ')
            .trim() ||
        userData?.username ||
        'My Account'

    const email = userData?.email ?? ''

    return (
        <DropdownMenu $open={open}>
            <ProfileSection>
                <ProfileAvatar
                    src={userData?.avatar?.url ?? avatarPlaceholder}
                    alt={displayName}
                    onError={e => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = avatarPlaceholder
                    }}
                />

                <ProfileDetails>
                    <ProfileName>{displayName}</ProfileName>
                    {email && <ProfileEmail>{email}</ProfileEmail>}
                </ProfileDetails>
            </ProfileSection>

            <DropdownDivider />

            <DropdownItem
                type="button"
                onClick={goToAccountSettings}
            >
                <ItemIcon>
                    <UserRound size={20} strokeWidth={1.8} />
                </ItemIcon>

                <ItemText>My Account</ItemText>

                <ChevronRight
                    size={18}
                    strokeWidth={1.8}
                    color="#828c94"
                />
            </DropdownItem>

            <DropdownDivider />

            <DropdownItem
                type="button"
                onClick={() => {
                    void handleLogout()
                }}
                disabled={isLoggingOut}
            >
                <ItemIcon $danger>
                    <LogOut size={20} strokeWidth={1.8} />
                </ItemIcon>

                <ItemText>{t('logout')}</ItemText>
            </DropdownItem>
        </DropdownMenu>
    )
}

interface DropdownMenuProps {
    $open: boolean
}

const DropdownMenu = styled.div<DropdownMenuProps>`
    position: absolute;
    top: 5rem;
    right: 0;
    z-index: 100;

    display: ${props => (props.$open ? 'block' : 'none')};

    width: 24rem;
    max-width: calc(100vw - 2rem);

    padding: 1.8rem 1.5rem;

    background: #ffffff;
    border: 1px solid #e3e6eb;
    border-radius: 1.8rem;

    box-shadow: none;
`

const ProfileSection = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;

    padding: 0.5rem 0.8rem 1rem;
`

const ProfileAvatar = styled.img`
    width: 4.8rem;
    height: 4.8rem;
    flex-shrink: 0;

    border-radius: 50%;
    object-fit: cover;

    background: #f3f6fb;
`

const ProfileDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    min-width: 0;
    flex: 1;
`

const ProfileName = styled.div`
    font-family: Montserrat, sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 1.8rem;
    color: #0d1444;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const ProfileEmail = styled.div`
    font-family: Montserrat, sans-serif;
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 1.6rem;
    color: #828c94;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const DropdownDivider = styled.div`
    height: 1px;
    margin: 1rem 0;
    background: #e5e8ed;
`

const DropdownItem = styled.button`
    display: flex;
    align-items: center;
    gap: 1.4rem;

    width: 100%;
    min-height: 4.8rem;

    padding: 1rem 0.8rem;

    border: none;
    border-radius: 1.5rem;
    background: transparent;

    text-align: left;
    cursor: pointer;

    transition: background-color 150ms ease;

@media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
        background: #f5f5f6;
    }
}
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`

const ItemIcon = styled.div<{ $danger?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    width: 2.2rem;
    height: 2.2rem;
    flex-shrink: 0;

    color: ${props => (props.$danger ? '#f04452' : '#536276')};
`

const ItemText = styled.span`
    flex: 1;

    font-family: Montserrat, sans-serif;
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 2rem;
    color: #0d1444;
`